import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import InputRange from 'react-input-range'
import { CloseLeftArrowButton } from '../../common/ui/Buttons'
import HotelNameField from '../fields/HotelName'
import Checkbox from '../../../common/forms/Checkbox'
import { canViewNonRefundableRates } from '../../helpers/userHelper'
import Button from '../../../common/ui/Button'

import { SEARCH, SearchContext } from "../SearchContext"

//eslint-disable-next-line
const StarRating = ({number, title, click, current}) => (
  <button
    className = {`star-group${current !== 0 && current <= number ? ' current' : ''}`}
    title     = { title }
    role      = "button"
    onClick   = { click }
    >
    <i className="fas fa-star" />
    <span>{number}</span>
  </button>
)

const FilterResults = ({
  open,
  toggleOpen
}) => {

  const { state: {
    filters,
    meta,
    user,
    defaultFilterValues,
    lastFilterSearch,
    nights
  }, dispatch } = useContext(SearchContext)
  const _setFilters = (payload) => dispatch({ type: SEARCH.FILTERS.SET, payload })
  const _setFiltersForSearch = (payload) => dispatch({ type: SEARCH.FILTERS_FOR_SEARCH.SET, payload })
  const _initiateFilterSearch = (payload) => dispatch({ type: SEARCH.FILTERS.SEARCH, payload })
  const [resetFilters, setResetFilters]     = useState(false)
  const [defaultFilters, setDefaultFilters] = useState({...defaultFilterValues})

  const _handleStarClick = (number) => setCurrentStar(number)

  const setCurrentStar = (number) => {
    let tmp = {...filters}
    tmp.starRating = number
    _setFilters(tmp)
  }

  useEffect(() => {
    // update max price in default filters
    let tmp = {...defaultFilterValues}
    let metaMaxPrice = parseInt(meta.max_price)
    let metaMinPrice = parseInt(meta.min_price)

    tmp.priceRange.max = metaMaxPrice
    tmp.priceRange.min = metaMinPrice
    setDefaultFilters(tmp)
    dispatch({type: SEARCH.FILTERS.DEFAULT, payload: tmp})

    if (filters.priceRange &&
      ((filters.priceRange.max > metaMaxPrice || filters.priceRange.max == 8000) ||
        (filters.priceRange.min < metaMinPrice)))  {

      let filtersTmp = {...filters}
      filtersTmp.priceRange = tmp.priceRange
      _setFilters(filtersTmp)
    }
  }, [meta.max_price, meta.min_price])

  const _setFilterPriceRange = (priceRange) => {
    let tmp = {...filters}
    tmp.priceRange = priceRange
    _setFilters(tmp)
  }

  useEffect(() => {
    // compare to default filters
    let equality = __compareFiltersToDefault()
    if(!equality && !resetFilters ) {
      setResetFilters(true)
    } else if(equality && resetFilters) {
      setResetFilters(false)
    }
  }, [filters])

  const __compareFiltersToDefault = () => isEqual(filters, defaultFilters)

  const _toggleNonRefundable = (includeNonRefundable) => {
    let tmp = {...defaultFilters}
    tmp.nonRefundable = includeNonRefundable
    _setFilters(tmp)
    let filtersForSearch = {...tmp}
    delete filtersForSearch.priceRange
    _setFiltersForSearch(filtersForSearch)
    _handleSubmit(false)
  }

  const _handleSubmit = (closeSearch = true) => {
    _initiateFilterSearch({value: true})
    if (closeSearch)
      toggleOpen(!open)
  }

  const _resetAllFilters = () => {
    let tmp = {...filters}
    tmp = defaultFilters
    _setFilters(tmp)

    if(!isEmpty(lastFilterSearch) && !isEqual(lastFilterSearch, defaultFilters)) {
      _initiateFilterSearch({value: true})
    }
  }

  return (
    <aside className={`filter-results animated slideInLeft ${open ? 'open' : 'closed'}`}>
      <CloseLeftArrowButton onClick={toggleOpen} />
      <h4 className="main-heading space-between">
        <span>Filter Results</span>
      </h4>
      <section className="hotel-name">
        <h4 className="space-between">
          <span>Hotel Name</span>
        </h4>
        <HotelNameField
          filters    = { filters }
          setFilters = { _setFilters }
          onSubmit   = { _handleSubmit }
        />
      </section>
      <section className="star-rating">
        <h4 className="space-between">
          <span>Star Rating</span>
        </h4>
        <div className="star-rating-group">
          { [1,2,3,4,5].map(i => {
            return (
              <StarRating
                number = {i}
                key = {i.toString()}
                title  = {i == 5 ? i.toString() : `${i} and up`}
                click  = {() => _handleStarClick(i)}
                current = {filters.starRating}
              />
            )
            })
          }
        </div>
      </section>

      {
        meta.min_price !== meta.max_price && (
        <section className="price-range">
          <h4 className="space-between">
            <span>Price Range</span>
          </h4>
          <p className="sub-heading">{nights == 1 ? "Per night" : `For ${nights} nights`}</p>
          <InputRange
            value     = { filters.priceRange }
            minValue  = { parseInt(meta.min_price) }
            maxValue  = { parseInt(meta.max_price) }
            onChange  = { value => {
              _setFilterPriceRange(value)
            }}
            formatLabel = {value => <span><em>$</em>{parseInt(value)}</span>}
          />
        </section>
      )}

      { canViewNonRefundableRates(user) && (
        <section className="rate-options">
          <h4>Rate Options</h4>
          <Checkbox
            handler   = {() => _toggleNonRefundable(true)}
            className = "non-refundable"
            label     = "Refundable &amp; Non-Refundable"
            checked   = {filters.nonRefundable}
            disabled  = {filters.nonRefundable}
          />
          <Checkbox
            handler   = {() => _toggleNonRefundable(false)}
            className = "refundable"
            label     = "Show Refundable Rates Only"
            checked   = {!filters.nonRefundable}
            disabled  = {!filters.nonRefundable}
          />
        </section>
      )}

      <div className="actions">
        <Button
          className     = "primary filter-me"
          role          = "submit"
          clickHandler  = {_handleSubmit}
        >
          Filter
        </Button>
        {
          resetFilters && (
          <Button
            className     = "secondary basic reset-filters"
            role          = "button"
            clickHandler  = { _resetAllFilters }
          >
            Reset
          </Button>
          )}
      </div>
    </aside>
  )

}

FilterResults.propTypes = {
  open: PropTypes.bool,
  toggleOpen: PropTypes.func.isRequired
}

export default FilterResults
